import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, field, IntegerDecoder, StringDecoder } from "@simonbackx/simple-encoding";
import { isSimpleError, SimpleError } from "@simonbackx/simple-errors";
import { Formatter, StringCompare } from "@stamhoofd/utility";
import { v4 as uuidv4 } from "uuid";
import { Address } from "../../addresses/Address";
import { CountryHelper } from "../../addresses/CountryDecoder";
import { Image } from "../../files/Image";
import { RecordChoice, RecordSettings, RecordType, RecordWarning, RecordWarningType } from "./RecordSettings";
export class RecordAnswer extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Date that this was changed. To determine merge order
         */
        this.date = new Date();
        /**
         * Date that this answer was last reviewed by the author
         * -> when editing by the organization, don't set this date
         */
        this.reviewedAt = null;
    }
    isOutdated(timeoutMs) {
        if (!this.reviewedAt) {
            return true;
        }
        if (this.reviewedAt.getTime() < new Date().getTime() - timeoutMs) {
            return true;
        }
        return false;
    }
    markReviewed() {
        this.reviewedAt = new Date();
    }
    get stringValue() {
        return "Onbekend";
    }
    get objectValue() {
        return this.stringValue;
    }
    /**
     * Include both the setting and the value
     */
    get descriptionValue() {
        return this.settings.name + ": " + this.stringValue;
    }
    get excelColumns() {
        return this.settings.excelColumns;
    }
    get excelValues() {
        return [{
                value: this.stringValue,
                format: null
            }];
    }
    getWarnings() {
        if (!this.isEmpty) {
            try {
                this.validate();
            }
            catch (e) {
                if (isSimpleError(e)) {
                    return [
                        RecordWarning.create({
                            id: 'validation-warning-' + this.id,
                            text: e.getHuman(),
                            type: RecordWarningType.Error
                        })
                    ];
                }
                // ignore
            }
        }
        return [];
    }
    validate() {
        // valid by default
    }
    /**
     * Return true when it is not the default value as a general rule
     * E.g. checkbox by default not checked -> empty if not checked
     */
    get isEmpty() {
        return false;
    }
    matchQuery(query) {
        return StringCompare.contains(this.stringValue, query);
    }
    isReviewedAfter(answer) {
        if (this.reviewedAt && answer.reviewedAt) {
            return this.reviewedAt > answer.reviewedAt;
        }
        if (this.reviewedAt && !answer.reviewedAt) {
            return true;
        }
        if (!this.reviewedAt && answer.reviewedAt) {
            return false;
        }
        // Both null
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordAnswer.prototype, "id", void 0);
__decorate([
    field({ decoder: RecordSettings })
], RecordAnswer.prototype, "settings", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 128 })
], RecordAnswer.prototype, "date", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], RecordAnswer.prototype, "reviewedAt", void 0);
export class RecordAnswerDecoderStatic {
    decode(data) {
        const type = data.field("settings").field("type").enum(RecordType);
        return data.decode(this.getClassForType(type));
    }
    getClassForType(type) {
        switch (type) {
            case RecordType.Checkbox: return RecordCheckboxAnswer;
            case RecordType.Text:
            case RecordType.Phone:
            case RecordType.Email:
            case RecordType.Textarea:
                return RecordTextAnswer;
            case RecordType.MultipleChoice: return RecordMultipleChoiceAnswer;
            case RecordType.ChooseOne: return RecordChooseOneAnswer;
            case RecordType.Address: return RecordAddressAnswer;
            case RecordType.Date: return RecordDateAnswer;
            case RecordType.Price: return RecordPriceAnswer;
            case RecordType.Image: return RecordImageAnswer;
            case RecordType.Integer: return RecordIntegerAnswer;
        }
        throw new SimpleError({
            code: "not_supported",
            message: "A property type is not supported",
            human: "Een bepaald kenmerk wordt niet ondersteund. Kijk na of je wel de laatste versie gebruikt en update indien nodig."
        });
    }
}
export const RecordAnswerDecoder = new RecordAnswerDecoderStatic();
function verifyBelgianNationalNumber(text) {
    const trimmed = text.replace(/[^A-Za-z0-9]+/g, ""); // keep A-Z for validation
    if (trimmed.length != 11) {
        return false;
    }
    const toCheck = parseInt(trimmed.substring(0, trimmed.length - 2));
    const checksum = parseInt(trimmed.substring(trimmed.length - 2));
    // we calculate the expected checksum. again
    const realChecksum = 97 - (toCheck % 97); // Dates before 2000
    const realChecksum2 = 97 - ((2000000000 + toCheck) % 97); // Dates after 2000
    return checksum === realChecksum || checksum === realChecksum2;
}
function formatBelgianNationalNumber(text) {
    const trimmed = text.replace(/[^A-Za-z0-9]+/g, ""); // keep A-Z for validation
    if (trimmed.length != 11) {
        return text;
    }
    // JJ.MM.DD-XXX.XX
    return trimmed.substring(0, 2) + '.' + trimmed.substring(2, 4) + '.' + trimmed.substring(4, 6) + '-' + trimmed.substring(6, 9) + '.' + trimmed.substring(9, 11);
}
export class RecordTextAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.value = null;
    }
    get stringValue() {
        var _a;
        return (_a = this.value) !== null && _a !== void 0 ? _a : "/";
    }
    get objectValue() {
        return this.value;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (!this.settings.warning) {
            return base;
        }
        if (this.settings.warning.inverted) {
            return this.value === null || this.value.length == 0 ? [this.settings.warning, ...base] : base;
        }
        return this.value !== null && this.value.length > 0 ? [this.settings.warning, ...base] : base;
    }
    validate() {
        if (this.settings.required && (this.value === null || this.value.length == 0)) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Dit veld is verplicht",
                field: "input"
            });
        }
        if (this.value && this.settings.name.toLocaleLowerCase().includes('rijksregisternummer')) {
            if (!verifyBelgianNationalNumber(this.value)) {
                throw new SimpleError({
                    code: "invalid_field",
                    message: "'" + (this.value) + "' is geen geldig rijksregisternummer. Je kan dit nummer vinden op je identiteitskaart, in de vorm van JJ.MM.DD-XXX.XX. Kijk na op typefouten.",
                    field: "input"
                });
            }
            // Auto format the number
            this.value = formatBelgianNationalNumber(this.value);
        }
    }
    get isEmpty() {
        return (this.value === null || this.value.length === 0);
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], RecordTextAnswer.prototype, "value", void 0);
export class RecordCheckboxAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.selected = false;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (!this.settings.warning) {
            return base;
        }
        if (this.settings.warning.inverted) {
            return !this.selected ? [this.settings.warning, ...base] : base;
        }
        return this.selected ? [this.settings.warning, ...base] : base;
    }
    get stringValue() {
        return this.selected ? "Aangevinkt" : "Niet aangevinkt";
    }
    get objectValue() {
        return this.selected;
    }
    get excelValues() {
        return [{
                value: this.selected ? (this.comments ? this.comments : "Ja") : "Nee",
                format: null
            }];
    }
    validate() {
        if (this.settings.required && !this.selected) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Dit is verplicht",
                field: "input"
            });
        }
    }
    get isEmpty() {
        return !this.selected;
    }
}
__decorate([
    field({ decoder: BooleanDecoder })
], RecordCheckboxAnswer.prototype, "selected", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], RecordCheckboxAnswer.prototype, "comments", void 0);
export class RecordMultipleChoiceAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.selectedChoices = [];
    }
    get stringValue() {
        return this.selectedChoices.map(c => c.name).join(", ");
    }
    get objectValue() {
        return this.selectedChoices.map(c => c.id);
    }
    getWarnings() {
        const base = super.getWarnings();
        if (this.selectedChoices.length == 0) {
            return base;
        }
        const warnings = base;
        for (const choice of this.selectedChoices) {
            if (choice.warning && !choice.warning.inverted) {
                warnings.push(choice.warning);
            }
        }
        for (const choice of this.settings.choices) {
            if (choice.warning && choice.warning.inverted) {
                if (!this.selectedChoices.find(s => s.id === choice.id)) {
                    warnings.push(choice.warning);
                }
            }
        }
        return warnings;
    }
    validate() {
        if (this.settings.required && this.selectedChoices.length == 0) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Duid minstens één keuze aan",
                field: "input"
            });
        }
        // Check selected choices still exist
        const cleanedChoices = [];
        let error = false;
        for (const choice of this.selectedChoices) {
            const c = this.settings.choices.find(c => c.id === choice.id);
            if (!c) {
                error = true;
            }
            else {
                cleanedChoices.push(c);
            }
        }
        this.selectedChoices = cleanedChoices;
        if (error) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Een keuze die je hebt gemaakt is niet meer beschikbaar. Kijk na en ga verder.",
                field: "input"
            });
        }
    }
    get isEmpty() {
        return this.selectedChoices.length === 0;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(RecordChoice) })
], RecordMultipleChoiceAnswer.prototype, "selectedChoices", void 0);
export class RecordChooseOneAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.selectedChoice = null;
    }
    get stringValue() {
        var _a, _b;
        return (_b = (_a = this.selectedChoice) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "/";
    }
    get objectValue() {
        var _a, _b;
        return (_b = (_a = this.selectedChoice) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (this.selectedChoice === null) {
            // TODO: show warning if inverted
            return base;
        }
        const warnings = base;
        if (this.selectedChoice.warning && !this.selectedChoice.warning.inverted) {
            warnings.push(this.selectedChoice.warning);
        }
        for (const choice of this.settings.choices) {
            if (choice.warning && choice.warning.inverted) {
                if (this.selectedChoice.id !== choice.id) {
                    warnings.push(choice.warning);
                }
            }
        }
        return warnings;
    }
    validate() {
        if (this.settings.required && this.selectedChoice === null) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Duid een keuze aan",
                field: "input"
            });
        }
        // Check selected choices still exist
        if (this.selectedChoice) {
            const id = this.selectedChoice.id;
            const c = this.settings.choices.find(c => c.id === id);
            if (!c) {
                this.selectedChoice = null;
                throw new SimpleError({
                    code: "invalid_field",
                    message: "Een keuze die je hebt gemaakt is niet meer beschikbaar. Kijk na en ga verder.",
                    field: "input"
                });
            }
            this.selectedChoice = c;
        }
    }
    get isEmpty() {
        return this.selectedChoice === null;
    }
}
__decorate([
    field({ decoder: RecordChoice, nullable: true })
], RecordChooseOneAnswer.prototype, "selectedChoice", void 0);
export class RecordAddressAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.address = null;
    }
    get stringValue() {
        var _a, _b;
        return (_b = (_a = this.address) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "/";
    }
    get objectValue() {
        var _a, _b;
        return (_b = (_a = this.address) === null || _a === void 0 ? void 0 : _a.encode({ version: 0 })) !== null && _b !== void 0 ? _b : null;
    }
    validate() {
        if (this.settings.required && this.address === null) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Verplicht in te vullen",
                field: "input"
            });
        }
    }
    get excelValues() {
        var _a, _b, _c, _d;
        return [
            {
                value: this.address ? `${this.address.street} ${this.address.number}` : '/',
                format: null
            },
            {
                value: (_b = (_a = this.address) === null || _a === void 0 ? void 0 : _a.postalCode) !== null && _b !== void 0 ? _b : '/',
                format: null
            },
            {
                value: (_d = (_c = this.address) === null || _c === void 0 ? void 0 : _c.city) !== null && _d !== void 0 ? _d : '/',
                format: null
            },
            {
                value: this.address ? CountryHelper.getName(this.address.country) : '/',
                format: null
            }
        ];
    }
    get isEmpty() {
        return this.address === null;
    }
}
__decorate([
    field({ decoder: Address, nullable: true })
], RecordAddressAnswer.prototype, "address", void 0);
export class RecordDateAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.dateValue = null;
    }
    get stringValue() {
        return this.dateValue ? Formatter.dateNumber(this.dateValue, true) : "/";
    }
    get objectValue() {
        return this.dateValue;
    }
    validate() {
        if (this.settings.required && this.dateValue === null) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Verplicht in te vullen",
                field: "input"
            });
        }
    }
    get isEmpty() {
        return this.dateValue === null;
    }
}
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], RecordDateAnswer.prototype, "dateValue", void 0);
export class RecordIntegerAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.value = null;
    }
    get stringValue() {
        return this.value !== null ? this.value.toString() : "/";
    }
    get objectValue() {
        return this.value;
    }
    getWarnings() {
        const base = super.getWarnings();
        if (!this.settings.warning) {
            return base;
        }
        if (this.settings.warning.inverted) {
            return this.value === null || this.value === 0 ? [this.settings.warning, ...base] : base;
        }
        return this.value !== null && this.value !== 0 ? [this.settings.warning, ...base] : base;
    }
    validate() {
        if (this.settings.required && (this.value === null)) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Dit veld is verplicht",
                field: "input"
            });
        }
    }
    get isEmpty() {
        return (this.value === null);
    }
}
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], RecordIntegerAnswer.prototype, "value", void 0);
export class RecordPriceAnswer extends RecordIntegerAnswer {
    get stringValue() {
        return this.value !== null ? Formatter.price(this.value) : "/";
    }
}
export class RecordImageAnswer extends RecordAnswer {
    constructor() {
        super(...arguments);
        this.image = null;
    }
    get stringValue() {
        var _a, _b;
        return (_b = (_a = this.image) === null || _a === void 0 ? void 0 : _a.getPublicPath()) !== null && _b !== void 0 ? _b : "/";
    }
    get objectValue() {
        var _a, _b;
        return (_b = (_a = this.image) === null || _a === void 0 ? void 0 : _a.encode({ version: 0 })) !== null && _b !== void 0 ? _b : null;
    }
    validate() {
        if (this.settings.required && this.image === null) {
            throw new SimpleError({
                code: "invalid_field",
                message: "Verplicht in te vullen",
                field: "input"
            });
        }
    }
    get isEmpty() {
        return this.image === null;
    }
}
__decorate([
    field({ decoder: Image, nullable: true })
], RecordImageAnswer.prototype, "image", void 0);
//# sourceMappingURL=RecordAnswer.js.map